import { Box, FormControlLabel, Link, Switch, Tab, Tabs, Typography } from "@mui/material";
import { useLocalStorageValue } from "@react-hookz/web";
import { useCallback, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router";
import { SearchField } from "../../search/search-field";
import { useRecentlyEdited } from "../hooks/use-recently-edited";
import { useUpcomingSends } from "../hooks/use-upcoming-sends";
import { CommunicationRow } from "./recently-edited/communication-row";
import { ContentFeed } from "./recently-edited/content-feed";
import { useContentTabs } from "./recently-edited/content-tabs";
import { FeedList } from "./recently-edited/feed-list";
import { FeedToggle } from "./recently-edited/feed-toggle";
import {
  RecentlyEditedOthersAlert,
  UpcomingSendsOthersAlert,
} from "./recently-edited/others-content-alert";
import { RecentlyEditedEmpty, UpcomingSendsEmpty } from "./recently-edited/recently-edited-empty";

import type { RecentlyEditedType } from "../hooks/use-recently-edited";
import type { ContentTab } from "./recently-edited/content-tabs";
import type { FeedView } from "./recently-edited/feed-toggle";
import type { FC } from "react";

/** The Communications, Surveys and Digital Assets most recently edited, to pick up where left. */
const RecentlyEditedFeed: FC<{
  mine: boolean;
  tab: ContentTab;
  onChanged: (entityType: RecentlyEditedType) => void;
  /** Turns My Content only off. */
  onViewAll: () => void;
}> = ({ mine, tab, onChanged, onViewAll }) => (
  <ContentFeed
    feed={useRecentlyEdited(mine, tab === "all" ? undefined : tab)}
    onChanged={onChanged}
    empty={
      <>
        {mine && (
          <RecentlyEditedOthersAlert
            entityType={tab === "all" ? undefined : tab}
            onViewAll={onViewAll}
          />
        )}
        <RecentlyEditedEmpty entityType={tab === "all" ? undefined : tab} />
      </>
    }
    errorMessage="Recently edited content couldn't be loaded."
    analyticsId="dashboard-recently-edited"
  />
);

/** The Communications sending soonest. Mounted only when picked, so it loads only then. */
const UpcomingSendsFeed: FC<{
  mine: boolean;
  onChanged: (entityType: RecentlyEditedType) => void;
  /** Turns My Content only off. */
  onViewAll: () => void;
}> = ({ mine, onChanged, onViewAll }) => {
  const feed = useUpcomingSends(mine);
  const onArchived = useCallback(() => onChanged("communication"), [onChanged]);

  return (
    <FeedList
      feed={feed}
      renderRow={(send, archived) => (
        <CommunicationRow
          communication={send}
          onArchived={archived}
          nextSendAt={send.next_send_at}
          analyticsId="dashboard-upcoming-sends"
        />
      )}
      onArchived={onArchived}
      empty={
        <>
          {mine && <UpcomingSendsOthersAlert onViewAll={onViewAll} />}
          <UpcomingSendsEmpty />
        </>
      }
      errorMessage="Upcoming sends couldn't be loaded."
      analyticsId="dashboard-upcoming-sends"
    />
  );
};

/** What was edited lately, or what sends next, to pick up where left or catch what's going out. */
export const RecentlyEditedSection: FC<{
  /** Something of this kind was archived or duplicated here. */
  onChanged: (entityType: RecentlyEditedType) => void;
}> = ({ onChanged }) => {
  // Remembered in this browser; react-hookz falls back on the default where storage is blocked.
  const { value: mine = true, set: setMine } = useLocalStorageValue<boolean>(
    "dashboard-recently-edited-mine",
    { defaultValue: true },
  );
  // Not remembered: the dashboard always opens on Recently edited, and on All.
  const [view, setView] = useState<FeedView>("recently-edited");
  const [tab, setTab] = useState<ContentTab>("all");
  const viewAll = useCallback(() => setMine(false), [setMine]);
  const tabs = useContentTabs();
  const searchLabel = tabs.find(({ value }) => value === tab)?.search ?? "Search all Content";

  const navigate = useNavigate();
  const search = useCallback(
    (value: string) => {
      const q = value.trim();
      if (!q) return;
      // Only the kind the feed shows.
      const params = new URLSearchParams({ q, ...(tab !== "all" && { type: tab }) });
      void navigate({ pathname: "/search", search: `?${params.toString()}` });
    },
    [navigate, tab],
  );

  return (
    <Box component="section">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <FeedToggle value={view} onChange={setView} />
        <Link
          component={RouterLink}
          to="/moments/history"
          variant="subtitle2"
          underline="hover"
          data-analytics-id="dashboard-recently-edited-sent-communications"
        >
          Sent Communications
        </Link>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          columnGap: 2,
          flexWrap: "wrap",
          // As tall as the tabs, so the switch stays put between views.
          minHeight: 48,
          my: 1,
        }}
      >
        {view === "recently-edited" ? (
          <Tabs
            value={tab}
            onChange={(_, value: ContentTab) => setTab(value)}
            aria-label="Recently edited content"
            variant="scrollable"
            scrollButtons={false}
          >
            {tabs.map(({ value, label }) => (
              <Tab
                key={value}
                value={value}
                label={label}
                data-analytics-id={`dashboard-recently-edited-tab-${label.toLowerCase().replaceAll(" ", "-")}`}
              />
            ))}
          </Tabs>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Does not include trigger-based sends
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: "auto" }}>
          <FormControlLabel
            label="My Content only"
            labelPlacement="start"
            control={
              <Switch
                size="small"
                checked={mine}
                onChange={(event) => setMine(event.target.checked)}
                data-analytics-id="dashboard-recently-edited-mine"
              />
            }
          />
          {view === "recently-edited" && (
            <SearchField
              label=""
              placeholder={searchLabel}
              title={searchLabel}
              slotProps={{ htmlInput: { "aria-label": searchLabel } }}
              size="small"
              defaultValue=""
              onSubmit={search}
              sx={{ width: 260 }}
            />
          )}
        </Box>
      </Box>

      {view === "recently-edited" ? (
        <RecentlyEditedFeed mine={mine} tab={tab} onChanged={onChanged} onViewAll={viewAll} />
      ) : (
        <UpcomingSendsFeed mine={mine} onChanged={onChanged} onViewAll={viewAll} />
      )}
    </Box>
  );
};
